import React from 'react';
import { LogLevel, LogEntry } from '../../../utils/GameLogger';
import { Badge, type BadgeTone } from '../../../game/ui/primitives';

interface LogEntryRowProps {
  log: LogEntry;
  index: number;
}

export const LogEntryRow: React.FC<LogEntryRowProps> = ({ log, index }) => {
  const getLevelBadgeTone = (level: LogLevel): BadgeTone => {
    switch (level) {
      case LogLevel.DEBUG: return 'neutral';
      case LogLevel.INFO: return 'info';
      case LogLevel.WARN: return 'warning';
      case LogLevel.ERROR: return 'danger';
      default: return 'neutral';
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString() + '.' + date.getMilliseconds().toString().padStart(3, '0');
  };

  return (
    <div
      style={{
        padding: '6px 12px',
        borderBottom: '1px solid rgba(255,255,255,0.03)',
        display: 'grid',
        gridTemplateColumns: 'min-content min-content 1fr',
        gap: '10px',
        fontSize: '11px',
        lineHeight: '1.5',
        alignItems: 'start',
        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
        transition: 'background 0.2s',
      }}
    >
      <span style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
        {formatTimestamp(log.timestamp).split(' ')[0]}
      </span>

      <Badge
        tone={getLevelBadgeTone(log.level)}
        size="sm"
        style={{ minWidth: '50px', justifyContent: 'center' }}
      >
        {LogLevel[log.level]}
      </Badge>

      <div style={{ wordBreak: 'break-word', color: '#e2e8f0' }}>
        {log.category && (
          <span style={{
            color: '#64748b',
            fontWeight: 'bold',
            marginRight: '8px',
            textTransform: 'uppercase',
            fontSize: '10px',
            letterSpacing: '0.5px'
          }}>
            [{log.category}]
          </span>
        )}
        <span style={{ fontFamily: 'monospace' }}>{log.message}</span>

        {log.data !== undefined && (
          <div style={{
            marginTop: '4px',
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: '4px',
            padding: '6px',
            border: '1px solid rgba(255,255,255,0.05)',
            overflowX: 'auto'
          }}>
            <pre style={{ margin: 0, color: '#94a3b8', fontSize: '10px' }}>
              {(() => {
                const seen = new WeakSet();
                return JSON.stringify(log.data, (_key, value) => {
                  if (typeof value === 'object' && value !== null) {
                    if (seen.has(value)) return '[Circular]';
                    seen.add(value);
                  }
                  return value;
                }, 2);
              })()}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};
